import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  collection, query, where, onSnapshot, doc, setDoc, updateDoc, deleteDoc, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { User } from '../services/firebaseService';

export interface GanttChart {
  id: string;
  name: string;
  userId: string;
  createdAt: any;
  updatedAt: any;
}

interface GanttChartsContextType {
  charts: GanttChart[];
  loading: boolean;
  createChart: (name: string) => Promise<string>;
  updateChart: (chartId: string, updates: Partial<GanttChart>) => Promise<void>;
  deleteChart: (chartId: string) => Promise<void>;
}

const GanttChartsContext = createContext<GanttChartsContextType | null>(null);

export const GanttChartsProvider: React.FC<{ user: User; children: React.ReactNode }> = ({ user, children }) => {
  const [charts, setCharts] = useState<GanttChart[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setCharts([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const q = query(
      collection(db, 'ganttCharts'),
      where('userId', '==', user.uid)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(d => ({ id: d.id, ...d.data() } as GanttChart));
      data.sort((a, b) => {
        const aTime = a.createdAt?.toMillis?.() ?? a.createdAt?.seconds * 1000 ?? 0;
        const bTime = b.createdAt?.toMillis?.() ?? b.createdAt?.seconds * 1000 ?? 0;
        return aTime - bTime;
      });
      setCharts(data);
      setLoading(false);
    }, () => setLoading(false));

    return unsubscribe;
  }, [user]);

  const createChart = async (name: string): Promise<string> => {
    if (!user) throw new Error('User not authenticated');
    const chartId = `gantt_${Date.now()}_${user.uid.substring(0, 8)}`;
    await setDoc(doc(db, 'ganttCharts', chartId), {
      id: chartId,
      name,
      userId: user.uid,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return chartId;
  };

  const updateChart = async (chartId: string, updates: Partial<GanttChart>): Promise<void> => {
    await updateDoc(doc(db, 'ganttCharts', chartId), { ...updates, updatedAt: serverTimestamp() });
  };

  const deleteChart = async (chartId: string): Promise<void> => {
    await deleteDoc(doc(db, 'ganttCharts', chartId));
  };

  return (
    <GanttChartsContext.Provider value={{ charts, loading, createChart, updateChart, deleteChart }}>
      {children}
    </GanttChartsContext.Provider>
  );
};

export const useGanttCharts = (): GanttChartsContextType => {
  const context = useContext(GanttChartsContext);
  if (!context) throw new Error('useGanttCharts must be used within a GanttChartsProvider');
  return context;
};
